import React, { useState, useMemo } from 'react';
import { ChemicalType, DailyReportRow, PreviousDayStock, Signatory } from '../types';
import { CHEMICALS } from '../constants/chemicals';
import { Download, ArrowUpDown, History, FileSpreadsheet, Printer, FileText } from 'lucide-react';
import * as XLSX from 'xlsx';
import { PrintReportModal } from './PrintReportModal';
import { exportReportToWord } from '../utils/exportWord';

interface ReportTableProps {
  chemical: ChemicalType | 'all';
  rows: DailyReportRow[];
  previousDayStock: PreviousDayStock;
  startDate: string;
  endDate: string;
  signatories: Signatory[];
  onOpenSignatorySettings: () => void;
}

export const ReportTable: React.FC<ReportTableProps> = ({
  chemical,
  rows,
  previousDayStock,
  startDate,
  endDate,
  signatories,
  onOpenSignatorySettings,
}) => {
  const [sortAsc, setSortAsc] = useState<boolean>(true);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState<boolean>(false);


  const formatNum = (val: number | null | undefined, decimals = 3) => {
    if (val === null || val === undefined || isNaN(val)) return '-';
    return Number(val).toLocaleString('th-TH', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  // Format date display (Thai buddhist or Gregorian readable)
  const formatDateDisplay = (dateStr: string) => {
    if (!dateStr) return '-';
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
  };

  // Sort rows
  const sortedRows = [...rows].sort((a, b) => {
    if (sortAsc) return a.date.localeCompare(b.date);
    return b.date.localeCompare(a.date);
  });

  // คำนวณผลต่าง (Difference) ตามลำดับวันตามปฏิทิน (Chronological Order)
  // สูตร: ผลต่าง = stock ของวันก่อนหน้า + รับเข้า - การใช้ - Stock คงเหลือปัจจุบัน (ต้องเป็น 0 เพื่อสมดุล stock)
  const diffMap = useMemo(() => {
    if (chemical === 'all') return {};

    const chronologicalRows = [...rows].sort((a, b) => a.date.localeCompare(b.date));
    const result: Record<string, number | null> = {};

    chronologicalRows.forEach((row, idx) => {
      const prevStockVal =
        idx === 0
          ? (previousDayStock[chemical] ?? null)
          : (chronologicalRows[idx - 1][chemical].stock ?? null);

      const income = row[chemical].income ?? 0;
      const usage = row[chemical].usage ?? 0;
      const currentStock = row[chemical].stock;

      if (prevStockVal !== null && currentStock !== null) {
        const rawDiff = prevStockVal + income - usage - currentStock;
        const cleanDiff = Math.abs(rawDiff) < 0.00001 ? 0 : rawDiff;
        result[row.date] = cleanDiff;
      } else {
        result[row.date] = null;
      }
    });

    return result;
  }, [rows, chemical, previousDayStock]);

  // Export to Excel
  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();

    if (chemical !== 'all') {
      const chemMeta = CHEMICALS[chemical];
      const data: any[] = [];

      // Add Previous Day Row
      if (previousDayStock.date) {
        data.push({
          วันที่: `${previousDayStock.date} (ยอดยกมา)`,
          'ปริมาณการรับ (ตัน)': '-',
          'ปริมาณการใช้ (ตัน)': '-',
          'ปริมาณ Stock คงเหลือ @ 24:00 (ตัน)': previousDayStock[chemical] ?? 0,
          'ผลต่าง (ตัน)': '-',
        });
      }

      // Add normal rows
      sortedRows.forEach((r) => {
        const dVal = diffMap[r.date];
        data.push({
          วันที่: r.date,
          'ปริมาณการรับ (ตัน)': r[chemical].income ?? 0,
          'ปริมาณการใช้ (ตัน)': r[chemical].usage ?? 0,
          'ปริมาณ Stock คงเหลือ @ 24:00 (ตัน)': r[chemical].stock ?? 0,
          'ผลต่าง (ตัน)': dVal !== null && dVal !== undefined ? dVal : '-',
        });
      });

      const ws = XLSX.utils.json_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, chemMeta.nameEn);
      XLSX.writeFile(wb, `รายงานสารเคมี_${chemMeta.nameEn}_${startDate}_ถึง_${endDate}.xlsx`);
    } else {
      // Export All Chemicals
      const data: any[] = [];

      if (previousDayStock.date) {
        data.push({
          วันที่: `${previousDayStock.date} (ยอดยกมา)`,
          'Alum รับ (ตัน)': '-', 'Alum ใช้ (ตัน)': '-', 'Alum Stock (ตัน)': previousDayStock.alum ?? 0,
          'PAC รับ (ตัน)': '-', 'PAC ใช้ (ตัน)': '-', 'PAC Stock (ตัน)': previousDayStock.pac ?? 0,
          'Chlorine รับ (ตัน)': '-', 'Chlorine ใช้ (ตัน)': '-', 'Chlorine Stock (ตัน)': previousDayStock.chl ?? 0,
          'Polymer รับ (ตัน)': '-', 'Polymer ใช้ (ตัน)': '-', 'Polymer Stock (ตัน)': previousDayStock.poly ?? 0,
        });
      }

      sortedRows.forEach((r) => {
        data.push({
          วันที่: r.date,
          'Alum รับ (ตัน)': r.alum.income ?? 0, 'Alum ใช้ (ตัน)': r.alum.usage ?? 0, 'Alum Stock (ตัน)': r.alum.stock ?? 0,
          'PAC รับ (ตัน)': r.pac.income ?? 0, 'PAC ใช้ (ตัน)': r.pac.usage ?? 0, 'PAC Stock (ตัน)': r.pac.stock ?? 0,
          'Chlorine รับ (ตัน)': r.chl.income ?? 0, 'Chlorine ใช้ (ตัน)': r.chl.usage ?? 0, 'Chlorine Stock (ตัน)': r.chl.stock ?? 0,
          'Polymer รับ (ตัน)': r.poly.income ?? 0, 'Polymer ใช้ (ตัน)': r.poly.usage ?? 0, 'Polymer Stock (ตัน)': r.poly.stock ?? 0,
        });
      });

      const ws = XLSX.utils.json_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, 'สารเคมีทั้งหมด');
      XLSX.writeFile(wb, `รายงานสารเคมีทั้งหมด_${startDate}_ถึง_${endDate}.xlsx`);
    }
  };

  // Export to CSV
  const handleExportCSV = () => {
    let csvContent = '\uFEFF'; // UTF-8 BOM for Excel Thai compatibility
    if (chemical !== 'all') {
      const chemMeta = CHEMICALS[chemical];
      csvContent += `วันที่,ปริมาณการรับ (ตัน),ปริมาณการใช้ (ตัน),ปริมาณ Stock คงเหลือ (ตัน),ผลต่าง (ตัน)\n`;

      if (previousDayStock.date) {
        csvContent += `"${previousDayStock.date} (ยอดยกมา)",-,-,${previousDayStock[chemical] ?? ''},-\n`;
      }

      sortedRows.forEach((r) => {
        const dVal = diffMap[r.date];
        csvContent += `"${r.date}",${r[chemical].income ?? 0},${r[chemical].usage ?? 0},${r[chemical].stock ?? ''},${dVal !== null && dVal !== undefined ? dVal : '-'}\n`;
      });

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `รายงานสารเคมี_${chemMeta.nameEn}_${startDate}_ถึง_${endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (chemical !== 'all') {
    const meta = CHEMICALS[chemical];
    const prevStock = previousDayStock[chemical];
    const totalIncome = rows.reduce((sum, r) => sum + (r[chemical].income ?? 0), 0);
    const totalUsage = rows.reduce((sum, r) => sum + (r[chemical].usage ?? 0), 0);
    const lastRow = rows.length > 0 ? rows[rows.length - 1] : null;
    const finalStock = lastRow ? lastRow[chemical].stock : null;

    const validDiffs = Object.values(diffMap).filter((d): d is number => d !== null);
    const totalDiff = validDiffs.reduce((sum, d) => sum + d, 0);
    const cleanTotalDiff = Math.abs(totalDiff) < 0.0001 ? 0 : totalDiff;

    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Table Action Header */}
        <div className="p-4 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-50/50">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-bold text-slate-800">
              ตารางรายการข้อมูล {meta.nameTh} ({meta.nameEn})
            </h2>
            <span className="text-xs px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-800 font-medium">
              หน่วย: ตัน (Tons)
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setIsPrintModalOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors cursor-pointer"
              title="พิมพ์รายงานหรือบันทึกเป็น PDF พร้อมลายเซ็น 4 คน"
            >
              <Printer className="w-3.5 h-3.5" />
              <span>พิมพ์ / PDF</span>
            </button>

            <button
              type="button"
              onClick={() => exportReportToWord({
                chemical,
                rows,
                previousDayStock,
                startDate,
                endDate,
                signatories,
              })}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
              title="ส่งออกเอกสาร Word (.docx) จัดรูปแบบตามแบบฟอร์ม"
            >
              <FileText className="w-3.5 h-3.5 text-blue-600" />
              <span>Word (.docx)</span>
            </button>

            <button
              type="button"
              onClick={() => setSortAsc(!sortAsc)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
            >
              <ArrowUpDown className="w-3.5 h-3.5" />
              <span>{sortAsc ? 'เรียง: เก่า → ใหม่' : 'เรียง: ใหม่ → เก่า'}</span>
            </button>

            <button
              type="button"
              onClick={handleExportExcel}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors cursor-pointer"
            >
              <FileSpreadsheet className="w-3.5 h-3.5" />
              <span>Export Excel</span>
            </button>

            <button
              type="button"
              onClick={handleExportCSV}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
            >
              <Download className="w-3.5 h-3.5" />
              <span>CSV</span>
            </button>
          </div>
        </div>

        {/* Table Content */}
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-left text-sm text-slate-600">
            <thead className="bg-slate-100/75 text-xs font-bold uppercase tracking-wider text-slate-700 border-b border-slate-200">
              <tr>
                <th scope="col" className="py-3 px-4 w-44">วันที่ (Date)</th>
                <th scope="col" className="py-3 px-4 text-right">ปริมาณการรับ (ตัน)</th>
                <th scope="col" className="py-3 px-4 text-right">ปริมาณการใช้ (ตัน)</th>
                <th scope="col" className="py-3 px-4 text-right">ปริมาณ Stock คงเหลือ (ตัน)</th>
                <th scope="col" className="py-3 px-4 text-right">ผลต่าง</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {/* Row 1: Previous Day Stock Highlighted */}
              {previousDayStock.date && (
                <tr className="bg-amber-50/60 font-medium hover:bg-amber-50 transition-colors border-b-2 border-amber-200/60">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <History className="w-4 h-4 text-amber-600 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-amber-900">
                          {formatDateDisplay(previousDayStock.date)}
                        </span>
                        <span className="ml-2 inline-block px-1.5 py-0.5 text-[10px] rounded bg-amber-200/70 text-amber-800 font-bold">
                          ยอดยกมา (วันก่อนหน้า)
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-right text-slate-400">-</td>
                  <td className="py-3 px-4 text-right text-slate-400">-</td>
                  <td className="py-3 px-4 text-right font-bold text-amber-900 bg-amber-100/40">
                    {formatNum(prevStock)}
                  </td>
                  <td className="py-3 px-4 text-right text-slate-400 text-xs italic">
                    -
                  </td>
                </tr>
              )}

              {/* Rows in Date Range */}
              {sortedRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-slate-500">
                    <div className="flex flex-col items-center justify-center gap-1.5">
                      <span className="text-base font-bold text-slate-700">ยังไม่มีข้อมูล</span>
                      <span className="text-xs text-slate-400">วันที่เลือกยังไม่มีข้อมูลบน BigQuery</span>
                    </div>
                  </td>
                </tr>
              ) : (
                sortedRows.map((row) => {
                  const dayData = row[chemical];
                  const diff = diffMap[row.date];

                  return (
                    <tr key={row.date} className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-3 px-4 font-medium text-slate-800">
                        {formatDateDisplay(row.date)}
                        <span className="ml-2 text-xs text-slate-400 font-normal">({row.date})</span>
                      </td>

                      {/* Income */}
                      <td className="py-3 px-4 text-right">
                        {(dayData.income ?? 0) > 0 ? (
                          <span className="inline-block px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 font-semibold text-xs border border-emerald-200">
                            +{formatNum(dayData.income)}
                          </span>
                        ) : (
                          <span className="text-slate-400">{formatNum(dayData.income)}</span>
                        )}
                      </td>

                      {/* Usage */}
                      <td className="py-3 px-4 text-right font-medium text-slate-700">
                        {(dayData.usage ?? 0) > 0 ? (
                          <span className="text-rose-600 font-semibold">
                            {formatNum(dayData.usage)}
                          </span>
                        ) : (
                          <span className="text-slate-400">{formatNum(dayData.usage)}</span>
                        )}
                      </td>

                      {/* Stock @ 24:00 */}
                      <td className="py-3 px-4 text-right font-bold text-blue-700 bg-blue-50/20">
                        {formatNum(dayData.stock)}
                      </td>

                      {/* ผลต่าง (Difference: stock วันก่อนหน้า + รับ - ใช้ - stock ปัจจุบัน) */}
                      <td className="py-3 px-4 text-right text-xs">
                        {diff === null || diff === undefined ? (
                          <span className="text-slate-400">-</span>
                        ) : diff === 0 ? (
                          <span className="inline-flex items-center gap-1 font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200" title="สต็อกสมดุลถูกต้อง (ผลต่าง = 0)">
                            0.000
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 font-bold text-rose-600 bg-rose-50 px-2 py-0.5 rounded border border-rose-200" title="สต็อกไม่สมดุล (ผลต่าง ≠ 0)">
                            {diff > 0 ? `+${formatNum(diff)}` : formatNum(diff)}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>

            {/* Footer Summary Row */}
            {sortedRows.length > 0 && (
              <tfoot className="bg-slate-100 font-semibold text-slate-800 border-t-2 border-slate-300">
                <tr>
                  <td className="py-3 px-4">
                    รวมทั้งสิ้น ({sortedRows.length} วัน)
                  </td>
                  <td className="py-3 px-4 text-right text-emerald-700 font-bold">
                    +{formatNum(totalIncome)}
                  </td>
                  <td className="py-3 px-4 text-right text-rose-700 font-bold">
                    {formatNum(totalUsage)}
                  </td>
                  <td className="py-3 px-4 text-right text-blue-800 font-bold bg-blue-50/60">
                    {formatNum(finalStock)}
                    <div className="text-[10px] text-slate-500 font-normal">ยอดคงเหลือล่าสุด</div>
                  </td>
                  <td className="py-3 px-4 text-right text-xs font-semibold">
                    {cleanTotalDiff === 0 ? (
                      <span className="inline-flex items-center gap-1 font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                        สมดุล (0.000)
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 font-bold text-rose-700 bg-rose-50 px-2 py-0.5 rounded border border-rose-200">
                        ผลต่างรวม: {cleanTotalDiff > 0 ? `+${formatNum(cleanTotalDiff)}` : formatNum(cleanTotalDiff)}
                      </span>
                    )}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        <PrintReportModal
          isOpen={isPrintModalOpen}
          onClose={() => setIsPrintModalOpen(false)}
          chemical={chemical}
          rows={rows}
          previousDayStock={previousDayStock}
          startDate={startDate}
          endDate={endDate}
          signatories={signatories}
          onOpenSignatorySettings={() => {
            setIsPrintModalOpen(false);
            onOpenSignatorySettings();
          }}
        />
      </div>
    );
  }

  // All Chemicals View
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="p-4 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-50/50">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-bold text-slate-800">
            ตารางเปรียบเทียบสารเคมีทั้งหมด 4 ชนิด (All Chemicals)
          </h2>
          <span className="text-xs px-2.5 py-0.5 rounded-full bg-slate-200 text-slate-700 font-medium">
            หน่วย: ตัน (Tons)
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto">
          <button
            type="button"
            onClick={() => setIsPrintModalOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors cursor-pointer"
            title="พิมพ์รายงานหรือบันทึกเป็น PDF พร้อมลายเซ็น 4 คน"
          >
            <Printer className="w-3.5 h-3.5" />
            <span>พิมพ์ / PDF</span>
          </button>

          <button
            type="button"
            onClick={() => exportReportToWord({
              chemical: 'alum',
              rows,
              previousDayStock,
              startDate,
              endDate,
              signatories,
            })}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
            title="ส่งออกเอกสาร Word (.docx)"
          >
            <FileText className="w-3.5 h-3.5 text-blue-600" />
            <span>Word (.docx)</span>
          </button>

          <button
            type="button"
            onClick={handleExportExcel}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors cursor-pointer"
          >
            <FileSpreadsheet className="w-3.5 h-3.5" />
            <span>Export Excel ทั้งหมด</span>
          </button>
        </div>
      </div>

      <div className="overflow-x-auto custom-scrollbar">
        <table className="w-full text-left text-xs text-slate-600">
          <thead className="bg-slate-100 text-slate-700 border-b border-slate-200">
            <tr>
              <th rowSpan={2} className="py-3 px-3 w-32 border-r border-slate-200 align-bottom">
                วันที่ (Date)
              </th>
              <th colSpan={3} className="py-2 px-3 text-center border-r border-slate-200 bg-blue-50/75 text-blue-800 font-bold">
                Alum (สารส้ม)
              </th>
              <th colSpan={3} className="py-2 px-3 text-center border-r border-slate-200 bg-emerald-50/75 text-emerald-800 font-bold">
                PAC (โพลีอะลูมิเนียมคลอไรด์)
              </th>
              <th colSpan={3} className="py-2 px-3 text-center border-r border-slate-200 bg-amber-50/75 text-amber-800 font-bold">
                Chlorine (คลอรีน)
              </th>
              <th colSpan={3} className="py-2 px-3 text-center bg-violet-50/75 text-violet-800 font-bold">
                Polymer (โพลิเมอร์)
              </th>
            </tr>
            <tr className="border-t border-slate-200 text-[11px] font-semibold text-slate-600">
              {/* Alum */}
              <th className="py-2 px-2 text-right bg-blue-50/30">รับ</th>
              <th className="py-2 px-2 text-right bg-blue-50/30">ใช้</th>
              <th className="py-2 px-2 text-right bg-blue-50/50 border-r border-slate-200 font-bold">Stock</th>

              {/* PAC */}
              <th className="py-2 px-2 text-right bg-emerald-50/30">รับ</th>
              <th className="py-2 px-2 text-right bg-emerald-50/30">ใช้</th>
              <th className="py-2 px-2 text-right bg-emerald-50/50 border-r border-slate-200 font-bold">Stock</th>

              {/* Chlorine */}
              <th className="py-2 px-2 text-right bg-amber-50/30">รับ</th>
              <th className="py-2 px-2 text-right bg-amber-50/30">ใช้</th>
              <th className="py-2 px-2 text-right bg-amber-50/50 border-r border-slate-200 font-bold">Stock</th>

              {/* Polymer */}
              <th className="py-2 px-2 text-right bg-violet-50/30">รับ</th>
              <th className="py-2 px-2 text-right bg-violet-50/30">ใช้</th>
              <th className="py-2 px-2 text-right bg-violet-50/50 font-bold">Stock</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100">
            {/* Previous Day Row */}
            {previousDayStock.date && (
              <tr className="bg-amber-50/70 font-semibold border-b border-amber-200">
                <td className="py-2.5 px-3 border-r border-slate-200 text-amber-900 whitespace-nowrap">
                  {formatDateDisplay(previousDayStock.date)} (ยอดยกมา)
                </td>

                <td className="py-2 px-2 text-right text-slate-400">-</td>
                <td className="py-2 px-2 text-right text-slate-400">-</td>
                <td className="py-2 px-2 text-right text-amber-900 border-r border-slate-200">{formatNum(previousDayStock.alum)}</td>

                <td className="py-2 px-2 text-right text-slate-400">-</td>
                <td className="py-2 px-2 text-right text-slate-400">-</td>
                <td className="py-2 px-2 text-right text-amber-900 border-r border-slate-200">{formatNum(previousDayStock.pac)}</td>

                <td className="py-2 px-2 text-right text-slate-400">-</td>
                <td className="py-2 px-2 text-right text-slate-400">-</td>
                <td className="py-2 px-2 text-right text-amber-900 border-r border-slate-200">{formatNum(previousDayStock.chl)}</td>

                <td className="py-2 px-2 text-right text-slate-400">-</td>
                <td className="py-2 px-2 text-right text-slate-400">-</td>
                <td className="py-2 px-2 text-right text-amber-900">{formatNum(previousDayStock.poly)}</td>
              </tr>
            )}

            {/* Daily Rows */}
            {sortedRows.length === 0 ? (
              <tr>
                <td colSpan={13} className="py-12 text-center text-slate-500">
                  <div className="flex flex-col items-center justify-center gap-1.5">
                    <span className="text-base font-bold text-slate-700">ยังไม่มีข้อมูล</span>
                    <span className="text-xs text-slate-400">วันที่เลือกยังไม่มีข้อมูลบน BigQuery</span>
                  </div>
                </td>
              </tr>
            ) : (
              sortedRows.map((r) => (
                <tr key={r.date} className="hover:bg-slate-50 transition-colors">
                <td className="py-2.5 px-3 font-medium text-slate-800 border-r border-slate-200 whitespace-nowrap">
                  {formatDateDisplay(r.date)}
                </td>

                {/* Alum */}
                <td className="py-2 px-2 text-right">{(r.alum.income ?? 0) > 0 ? `+${formatNum(r.alum.income)}` : '-'}</td>
                <td className="py-2 px-2 text-right text-rose-600 font-medium">{(r.alum.usage ?? 0) > 0 ? formatNum(r.alum.usage) : '-'}</td>
                <td className="py-2 px-2 text-right font-bold text-blue-700 bg-blue-50/20 border-r border-slate-200">{formatNum(r.alum.stock)}</td>

                {/* PAC */}
                <td className="py-2 px-2 text-right">{(r.pac.income ?? 0) > 0 ? `+${formatNum(r.pac.income)}` : '-'}</td>
                <td className="py-2 px-2 text-right text-rose-600 font-medium">{(r.pac.usage ?? 0) > 0 ? formatNum(r.pac.usage) : '-'}</td>
                <td className="py-2 px-2 text-right font-bold text-emerald-700 bg-emerald-50/20 border-r border-slate-200">{formatNum(r.pac.stock)}</td>

                {/* Chlorine */}
                <td className="py-2 px-2 text-right">{(r.chl.income ?? 0) > 0 ? `+${formatNum(r.chl.income)}` : '-'}</td>
                <td className="py-2 px-2 text-right text-rose-600 font-medium">{(r.chl.usage ?? 0) > 0 ? formatNum(r.chl.usage) : '-'}</td>
                <td className="py-2 px-2 text-right font-bold text-amber-700 bg-amber-50/20 border-r border-slate-200">{formatNum(r.chl.stock)}</td>

                {/* Polymer */}
                <td className="py-2 px-2 text-right">{(r.poly.income ?? 0) > 0 ? `+${formatNum(r.poly.income)}` : '-'}</td>
                <td className="py-2 px-2 text-right text-rose-600 font-medium">{(r.poly.usage ?? 0) > 0 ? formatNum(r.poly.usage) : '-'}</td>
                <td className="py-2 px-2 text-right font-bold text-violet-700 bg-violet-50/20">{formatNum(r.poly.stock)}</td>
              </tr>
            ))
          )}
          </tbody>
        </table>
      </div>

      <PrintReportModal
        isOpen={isPrintModalOpen}
        onClose={() => setIsPrintModalOpen(false)}
        chemical="alum"
        rows={rows}
        previousDayStock={previousDayStock}
        startDate={startDate}
        endDate={endDate}
        signatories={signatories}
        onOpenSignatorySettings={() => {
          setIsPrintModalOpen(false);
          onOpenSignatorySettings();
        }}
      />
    </div>
  );
};

